import { useEffect, useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import colors from '@Constants/colors'; // Assurez-vous que ce fichier existe
import { faMagnifyingGlassLocation, faEraser } from '@fortawesome/free-solid-svg-icons';
import Button from 'react-bootstrap/Button';

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 15px;
  right: 15px;
  z-index: 21000;
`;

const Icon = styled(Button)`
  background-color: ${colors.WHITE};
  border-color: ${colors.TERTIARY};
  border-radius: 50%;
  z-index: 20000;
  width: 55px;
  padding: 15px 0;

  &:hover {
    border-color: ${colors.TERTIARY};
    background-color: ${colors.TERTIARY};
  }
`;

const SearchIcon = styled(FontAwesomeIcon)`
  color: ${colors.TERTIARY};

  &:hover {
    color: ${colors.WHITE};
  }
`;

const EraseIcon = styled(FontAwesomeIcon)`
  color: ${colors.GREY};
  display: ${props => (props.$visibility ? 'block' : 'none')};
  z-index: 20002;
  user-select: none;
  padding: 7px 4px;
  background-color: ${colors.WHITE};
  cursor: pointer;
`;

const FindInput = styled.input`
  display: ${props => (props.$visibility ? 'block' : 'none')};
  background-color: ${colors.WHITE};
  border-color: ${colors.TERTIARY};
  border-width: 1px;
  border-radius: 5px;
  border-style: solid;
  margin-right: -36px;
  min-width: 250px;
  padding: 5px;
  z-index: 20001;

  &:focus {
    outline: none;
    border-width: 2px;
    border-radius: 5px;
    border-style: solid;
  }
`;

const Label = styled.label`
  position: absolute;
  pointer-events: none;
  left: 10px;
  display: ${props => (props.$visibility ? 'block' : 'none')};
  z-index: 20002;
  user-select: none;

  ${props =>
    props.$value
      ? `top: -10px; font-size: 14px; color: ${colors.PRIMARY}; font-weight: bold;`
      : `color: ${colors.GREY}; font-size: 16px; font-weight: normal; top: 15px;`}

  input:focus ~ & {
    transition: 0.2s ease all;
    top: -10px;
    font-size: 14px;
    color: ${colors.PRIMARY};
    font-weight: bold;
  }
`;

const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: ${colors.WHITE};
  border: 1px solid ${colors.TERTIARY};
  border-radius: 5px;
  margin: 2px 0 0 0;
  padding: 0;
  list-style: none;
  max-height: 200px;
  overflow-y: auto;
  z-index: 20003;
`;

const SuggestionItem = styled.li`
  padding: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${colors.LIGHT_GREY || '#f0f0f0'};
  }
`;

function InputFindAddress(props) {
  const {
    show,
    name,
    label,
    value,
    visible,
    onChange,
    onSelect,
    bpOnClick,
    eraseOnClick,
  } = props;

  const findInputRef = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Focus automatique quand l'input devient visible
  useEffect(() => {
    if (visible) {
      findInputRef.current.focus();
    }
  }, [visible]);

  // Appel API avec debounce
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (value.length > 2) {
        fetchAddresses();
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [value]);

  const fetchAddresses = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`https://api-adresse.data.gouv.fr/search?q=${encodeURIComponent(value)}`);
      const data = await response.json();
      setSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Erreur lors de la récupération des adresses:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    console.log('handleSuggestionClick', suggestion);
    setShowSuggestions(false);
    setSuggestions([]);
    const place = {
      formatted_address: suggestion.properties.label,
      geometry: {
        location: {
          lat: () => suggestion.geometry.coordinates[1],
          lng: () => suggestion.geometry.coordinates[0],
        },
      },
    };
    onSelect(place); // Appelle onSelect avec un objet compatible
    onChange({ target: { name, value: suggestion.properties.label } }); // Met à jour la valeur de l'input
  };

  const handleBlur = () => {
    setTimeout(() => setShowSuggestions(false), 200);
  };

  const ChangeOnHover = () => {
    document.getElementById('FindAddress').style.color = colors.WHITE;
  };

  const ChangeOnOut = () => {
    document.getElementById('FindAddress').style.color = colors.TERTIARY;
  };

  return (
    <>
      {show && (
        <Wrapper>
          <FindInput
            $visibility={visible}
            ref={findInputRef}
            name={name}
            type="text"
            value={value}
            onChange={onChange}
            onFocus={() => setShowSuggestions(true)}
            onBlur={handleBlur}
            placeholder=""
          />
          <Label htmlFor={name} $value={value} $visibility={visible}>
            {label}
          </Label>
          {visible && suggestions.length > 0 && showSuggestions && (
            <SuggestionsList>
              {isLoading ? (
                <SuggestionItem>Chargement...</SuggestionItem>
              ) : (
                suggestions.slice(0, 5).map((suggestion, index) => (
                  <SuggestionItem
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    {suggestion.properties.label}
                  </SuggestionItem>
                ))
              )}
            </SuggestionsList>
          )}
          <EraseIcon
            icon={faEraser}
            size="lg"
            $visibility={visible}
            onClick={() => eraseOnClick(findInputRef)}
          />
          <Icon onMouseOver={ChangeOnHover} onMouseOut={ChangeOnOut} onClick={bpOnClick}>
            <SearchIcon id="FindAddress" icon={faMagnifyingGlassLocation} size="xl" />
          </Icon>
        </Wrapper>
      )}
    </>
  );
}

export default InputFindAddress;